import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { CreditCard, AlertTriangle, Clock3, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr, peso } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { NeedTenant } from "../components/layout/AppShell";

const DURATIONS = [1, 3, 6, 12];
const REQ_STATUS = { pending: "bg-blue-100 text-blue-700", approved: "bg-emerald-100 text-emerald-700", rejected: "bg-red-100 text-red-700" };

function addMonths(months) {
  const d = new Date();
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
}

function addDays(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function SubscriptionPage() {
  const { user, activeTenantId, subscription, checkAuth } = useAuth();
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(subscription);
  const [requests, setRequests] = useState([]);
  const [subscribing, setSubscribing] = useState(null); // plan object
  const [duration, setDuration] = useState("1");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: p }, { data: s }, { data: r }] = await Promise.all([
        api.get("/subscriptions/plans"), api.get("/subscription"), api.get("/subscription/requests"),
      ]);
      setPlans(p);
      setSub(s);
      setRequests(r);
    } catch (e) { toast.error(fmtErr(e)); }
  }, []);
  useEffect(() => {
    if (user.role === "system_admin" && !activeTenantId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, activeTenantId]);

  if (user.role === "system_admin" && !activeTenantId) return <NeedTenant />;

  const currentId = sub?.plan_id || "free";
  const isOwner = user.role === "owner" || user.role === "system_admin";
  const reminderDays = currentId === "free" ? 2 : 7;
  const hasPending = requests.some((r) => r.status === "pending");
  const trialUsed = !!sub?.trial_used;

  const subscribe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/subscription/request", {
        plan_id: subscribing.plan_id,
        duration_months: subscribing.plan_id === "free" ? 0 : Number(duration),
      });
      if (data.activated) {
        toast.success("14-day free trial started!");
        checkAuth();
      } else {
        toast.success("Subscription request submitted — pending admin approval");
      }
      setSubscribing(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-8" data-testid="subscription-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Subscription</h1>
        <p className="text-sm text-slate-500">Choose a plan — activation is confirmed manually after payment</p>
      </div>

      {sub?.locked && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-5 flex items-start gap-3" data-testid="subscription-expired-banner">
          <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
          <div>
            <p className="font-semibold text-red-800">{sub.plan_id === "free" ? "Free trial ended" : "Subscription expired"}</p>
            <p className="text-sm text-red-700">All modules are locked. Choose a plan below — your subscription activates once payment is approved by the platform admin.</p>
          </div>
        </div>
      )}
      {sub && !sub.locked && sub.days_remaining !== null && sub.days_remaining <= reminderDays && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-5 flex items-start gap-3" data-testid="subscription-reminder-banner">
          <Clock3 className="h-5 w-5 text-amber-600 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-800">Renewal reminder</p>
            <p className="text-sm text-amber-700">
              {sub.plan_id === "free" ? "Your 14-day free trial" : "Your subscription"} expires in {sub.days_remaining} day{sub.days_remaining === 1 ? "" : "s"} ({sub.expires_at?.slice(0, 10)}). Renew now to avoid interruption.
            </p>
          </div>
        </div>
      )}

      {sub && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 flex flex-wrap items-center gap-6" data-testid="current-subscription-card">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-lg bg-slate-900 flex items-center justify-center">
              <CreditCard className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="font-heading font-bold text-lg text-slate-900">{sub.plan_name}</p>
              <p className="text-xs text-slate-500">{sub.plan_id === "free" ? "14-day free trial" : `${peso(sub.price_monthly)}/month`}</p>
            </div>
          </div>
          <div className="text-sm text-slate-600">
            <p>Expiry: <span className="font-mono font-medium" data-testid="subscription-expiry-date">{sub.expires_at ? sub.expires_at.slice(0, 10) : "No expiry"}</span></p>
            {sub.days_remaining !== null && <p>Days remaining: <span className="font-mono font-medium">{sub.days_remaining}</span></p>}
          </div>
          <span className={`rounded-full px-3 py-1 text-xs font-bold ${sub.locked ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
            {sub.locked ? "EXPIRED" : "ACTIVE"}
          </span>
        </div>
      )}

      {requests.some((r) => r.status === "pending") && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800 flex items-start gap-2.5" data-testid="pending-request-notice">
          <Clock3 className="h-4 w-4 mt-0.5 shrink-0" />
          <span>A subscription request is <span className="font-semibold">pending approval</span>. You cannot submit another payment request unless the pending one is declined.</span>
        </div>
      )}

      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {plans.map((p) => (
          <div key={p.plan_id} data-testid={`plan-card-${p.plan_id}`}
            className={`bg-white rounded-xl border p-6 space-y-4 flex flex-col ${p.plan_id === currentId ? "border-slate-900 ring-1 ring-slate-900" : "border-slate-200"}`}>
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-bold text-slate-900">{p.name}</h3>
              {p.plan_id === currentId && <span className="rounded bg-slate-900 text-white px-2 py-0.5 text-[10px] font-bold">CURRENT</span>}
            </div>
            <p className="font-mono font-bold text-2xl text-slate-950">
              {p.plan_id === "free" ? "₱0" : peso(p.price_monthly)}
              <span className="text-xs font-normal text-slate-400">{p.plan_id === "free" ? " · 14-day trial" : "/mo"}</span>
            </p>
            <ul className="space-y-1.5 text-sm text-slate-600 flex-1">
              {p.features.map((f) => <li key={f} className="flex gap-2"><span className="text-emerald-600">✓</span>{f}</li>)}
            </ul>
            {isOwner && (
              <div className="space-y-1.5">
                <Button
                  className={p.plan_id === currentId ? "w-full" : "w-full bg-slate-900 hover:bg-slate-800"}
                  variant={p.plan_id === currentId ? "outline" : "default"}
                  disabled={hasPending || (p.plan_id === "free" && trialUsed)}
                  onClick={() => { setSubscribing(p); setDuration("1"); }}
                  data-testid={`subscribe-btn-${p.plan_id}`}
                >
                  {p.plan_id === "free" ? (trialUsed ? "Trial already availed" : "Start free trial") : p.plan_id === currentId ? "Renew / Extend" : "Subscribe"}
                </Button>
                {p.plan_id === "free" && trialUsed && (
                  <p className="text-[11px] text-slate-400 text-center" data-testid="trial-used-note">Free trial already used for this store — choose a paid plan to continue.</p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {requests.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3" data-testid="subscription-requests-list">
          <h3 className="font-heading font-semibold text-slate-900">My Subscription Requests</h3>
          {requests.map((r) => (
            <div key={r.request_id} className="flex flex-wrap items-center gap-3 border-b border-slate-50 pb-2.5 last:border-0 text-sm">
              <span className="font-semibold text-slate-900">{r.plan_name}</span>
              <span className="text-slate-500 text-xs">{r.duration_months > 0 ? `${r.duration_months} mo · ${peso(r.amount)}` : "14-day trial"}</span>
              <span className="text-xs text-slate-400">{r.created_at?.slice(0, 10)}</span>
              <span className={`rounded px-2 py-0.5 text-[10px] font-bold uppercase ${REQ_STATUS[r.status]}`} data-testid={`request-status-${r.request_id}`}>{r.status}</span>
            </div>
          ))}
        </div>
      )}

      <p className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3">
        Payments are manual: pay via QRPh / GCash and send your proof of payment. The platform admin approves and activates your subscription.
        {user.role === "system_admin" && (
          <> Review requests in the <Link to="/admin" className="font-semibold text-slate-900 underline">Admin Console → Sub Requests</Link>.</>
        )}
      </p>

      <Dialog open={!!subscribing} onOpenChange={(o) => !o && setSubscribing(null)}>
        <DialogContent className="max-w-sm" data-testid="subscribe-dialog">
          <DialogHeader><DialogTitle className="font-heading">Subscribe — {subscribing?.name}</DialogTitle></DialogHeader>
          {subscribing && (
            <div className="space-y-4">
              {subscribing.plan_id === "free" ? (
                <div className="rounded-lg border border-slate-200 p-3.5 text-sm flex justify-between items-center">
                  <span>14-day free trial</span>
                  <span className="font-mono text-xs text-slate-500">expires {addDays(14)}</span>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Duration — each option shows its expiry date</Label>
                  <RadioGroup value={duration} onValueChange={setDuration} className="space-y-2">
                    {DURATIONS.map((m) => (
                      <label key={m} className={`flex items-center justify-between rounded-lg border px-3.5 py-2.5 text-sm cursor-pointer ${duration === String(m) ? "border-slate-900 bg-slate-50" : "border-slate-200"}`} data-testid={`duration-option-${m}`}>
                        <span className="flex items-center gap-2"><RadioGroupItem value={String(m)} />{m} month{m > 1 ? "s" : ""}</span>
                        <span className="text-right">
                          <span className="font-mono font-semibold">{peso(subscribing.price_monthly * m)}</span>
                          <span className="block text-[10px] text-slate-400">expires {addMonths(m)}</span>
                        </span>
                      </label>
                    ))}
                  </RadioGroup>
                </div>
              )}
              <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2.5 py-2 leading-relaxed">
                After submitting, pay via QRPh / GCash and send your proof of payment. The admin activates your subscription upon approval.
              </p>
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={subscribe} disabled={busy} data-testid="subscribe-confirm-btn">
                {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {subscribing.plan_id === "free" ? "Start trial" : "Submit subscription request"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
